import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useState, useEffect, useCallback, useMemo } from "react";
import { useThemeFonts, useThemeColors } from "@brandclub/bc-widgets";
import { useVoiceSession } from "./hooks/useVoiceSession.js";
import { useVoiceSoundEffects } from "./hooks/useVoiceSoundEffects.js";
import { ControlBar } from "./components/ControlBar.js";
import { VoiceBottomDock } from "./components/VoiceBottomDock.js";
import { VoiceScrollList } from "./components/VoiceScrollList.js";
import { VoiceThemeProvider, useVoiceThemeContext } from "./theme/VoiceThemeProvider.js";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog.js";
function resolveBodyFontFamily(bodyFont) {
  if (!bodyFont) return void 0;
  return bodyFont.format === "system" ? bodyFont.url : `"${bodyFont.name}", sans-serif`;
}
const EMPTY_LINES = [];
const EMPTY_WIDGET_EVENTS = [];
function deriveBlobState(args) {
  if (args.phase === "error") return "error";
  if (args.phase === "ended") return "ended";
  if (args.phase === "requesting-permission" || args.phase === "connecting" || args.phase === "ending") {
    return "connecting";
  }
  if (args.phase !== "live") return "connecting";
  if (args.muted) return "muted";
  if (args.hasStatus) return "tool";
  if (args.isUserSpeaking) return "listening";
  return "idle";
}
function deriveControlBarMode(phase) {
  if (phase === "live") return "live";
  if (phase === "ending") return "ending";
  return "connecting";
}
const VoiceCallOverlayInner = ({ conversationId, pdpStacklineSku, onEnded, onUserEndClick }) => {
  var _a, _b;
  const errorHeadline = "Voice mode unavailable";
  const { visual } = useVoiceThemeContext();
  const blobEnabled = (visual == null ? void 0 : visual.blobEnabled) ?? false;
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const dialogBg = themeColors.background || "#ffffff";
  const dialogPrimary = themeColors.textPrimary || themeColors.primary || "#0d2340";
  const dialogPrimaryFg = themeColors.buttonText || "#ffffff";
  const dialogBodyFont = themeFonts == null ? void 0 : themeFonts.body;
  const dialogFontFamily = dialogBodyFont ? dialogBodyFont.format === "system" ? dialogBodyFont.url : `"${dialogBodyFont.name}", sans-serif` : void 0;
  const [inputText, setInputText] = useState("");
  const sfx = useVoiceSoundEffects();
  const handleEnded = useCallback(
    (info) => {
      if (info.reason !== "ended_by_model") {
        sfx.playEndCall();
      }
      onEnded(info);
    },
    [onEnded, sfx]
  );
  const session = useVoiceSession({
    conversationId,
    pdpStacklineSku,
    onEnded: handleEnded,
    onEndCallInitiated: () => sfx.playEndCall()
  });
  const toolStatus = (_b = (_a = session.lastStatusEvent) == null ? void 0 : _a.data) == null ? void 0 : _b.status;
  const pendingStatus = session.phase === "live" && (session.toolCallsInFlight > 0 || !!toolStatus) ? toolStatus || "Thinking…" : null;
  const connectingStatus = session.phase === "requesting-permission" || session.phase === "connecting" ? "Connecting…" : null;
  const shouldPlayToolLoop = session.phase === "live" && session.toolCallsInFlight > 0 && !session.isAssistantAudible;
  useEffect(() => {
    if (shouldPlayToolLoop) {
      sfx.startToolLoop();
    } else {
      sfx.stopToolLoop();
    }
  }, [shouldPlayToolLoop, sfx]);
  const blobState = useMemo(
    () => deriveBlobState({
      phase: session.phase,
      muted: session.muted,
      isUserSpeaking: session.isUserSpeaking,
      hasStatus: !!toolStatus
    }),
    [session.phase, session.muted, session.isUserSpeaking, toolStatus]
  );
  const controlBarMode = deriveControlBarMode(session.phase);
  const handleEnd = useCallback(async () => {
    await session.end("user_ended");
  }, [session]);
  const handleSubmitText = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || session.phase !== "live") return;
    session.sendText(trimmed);
    setInputText("");
  }, [inputText, session]);
  const waveformActive = session.isAssistantAudible || session.isUserSpeaking || session.toolCallsInFlight > 0;
  return /* @__PURE__ */ jsxs("div", { style: pageStyle, children: [
    /* @__PURE__ */ jsxs("div", { style: liveScrollWrapStyle, children: [
      session.phase === "live" ? /* @__PURE__ */ jsx(
        VoiceScrollList,
        {
          lines: session.lines,
          widgetEvents: session.widgetEvents,
          pendingStatus,
          onLoginCompleted: session.notifyLoginCompleted,
          onLoginFailed: session.notifyLoginFailed
        }
      ) : connectingStatus ? /* @__PURE__ */ jsx(VoiceScrollList, { lines: EMPTY_LINES, widgetEvents: EMPTY_WIDGET_EVENTS, pendingStatus: connectingStatus }) : null,
      blobEnabled ? /* @__PURE__ */ jsx(VoiceBottomDock, { blobState }) : null
    ] }),
    /* @__PURE__ */ jsx(
      ControlBar,
      {
        mode: controlBarMode,
        value: inputText,
        onChange: setInputText,
        onSubmit: handleSubmitText,
        muted: session.muted,
        onToggleMute: () => session.setMuted(!session.muted),
        onEnd: handleEnd,
        active: waveformActive,
        toolActive: session.toolCallsInFlight > 0,
        micDisabled: session.phase !== "live"
      }
    ),
    /* @__PURE__ */ jsx(
      ConfirmActionDialog,
      {
        open: session.phase === "error",
        title: session.errorTitle ?? errorHeadline,
        message: session.error ?? "Could not start a call.",
        confirmText: "Try again",
        cancelText: "Back to chat",
        backgroundColor: dialogBg,
        primaryColor: dialogPrimary,
        primaryForegroundColor: dialogPrimaryFg,
        fontFamily: dialogFontFamily,
        onConfirm: session.retry,
        onCancel: onUserEndClick
      }
    )
  ] });
};
const OVERLAY_FADE_MS = 200;
const VoiceCallOverlay = ({
  conversationId,
  open,
  onClose,
  pdpStacklineSku,
  containerScoped = false
}) => {
  const { themeFonts } = useThemeFonts();
  const [closing, setClosing] = useState(false);
  const closeTimerRef = React__default.useRef(void 0);
  useEffect(
    () => () => {
      if (closeTimerRef.current !== void 0) clearTimeout(closeTimerRef.current);
    },
    []
  );
  const fadeThenClose = useCallback(
    (info) => {
      if (closeTimerRef.current !== void 0) return;
      setClosing(true);
      closeTimerRef.current = setTimeout(() => onClose(info), OVERLAY_FADE_MS);
    },
    [onClose]
  );
  if (!open) return null;
  if (!conversationId) return null;
  const brandFontFamily = resolveBodyFontFamily(themeFonts == null ? void 0 : themeFonts.body);
  const baseShellStyle = containerScoped ? {
    position: "absolute",
    inset: 0,
    // Must sit above ALL chat-column content. The overlay is spatially
    // confined to the column (inset:0), so a high z-index can't bleed onto
    // the sidebar/header (separate DOM regions). zIndex:1 was too low — the
    // federated compare-products widget's sticky attribute/label columns
    // use z-index:2 and poked through; the desktop share button uses 50.
    zIndex: 100,
    background: "var(--color-background, #FFFFFF)",
    display: "flex",
    flexDirection: "column",
    borderRadius: "inherit",
    overflow: "hidden",
    ...brandFontFamily ? { fontFamily: brandFontFamily } : {}
  } : {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    background: "var(--color-background, #FFFFFF)",
    display: "flex",
    flexDirection: "column",
    ...brandFontFamily ? { fontFamily: brandFontFamily } : {}
  };
  const shellStyle = {
    ...baseShellStyle,
    opacity: closing ? 0 : 1,
    transition: `opacity ${OVERLAY_FADE_MS}ms ease`,
    // Don't intercept clicks meant for the revealed chat while fading out.
    pointerEvents: closing ? "none" : void 0
  };
  return /* @__PURE__ */ jsx(VoiceThemeProvider, { children: /* @__PURE__ */ jsx("div", { style: shellStyle, role: "dialog", "aria-modal": "true", "aria-label": "Voice call", children: /* @__PURE__ */ jsx(
    VoiceCallOverlayInner,
    {
      conversationId,
      pdpStacklineSku,
      onEnded: (info) => fadeThenClose({
        reason: info.reason,
        durationSec: info.durationSec,
        lines: info.lines,
        widgetEvents: info.widgetEvents
      }),
      onUserEndClick: () => fadeThenClose({ reason: "user_dismissed_error" })
    }
  ) }) });
};
const pageStyle = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  background: "transparent"
};
const liveScrollWrapStyle = {
  position: "relative",
  flex: "1 1 auto",
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};
export {
  VoiceCallOverlay
};
//# sourceMappingURL=VoiceCallOverlay.js.map
